// src/index.tsx
import React from "react";
import { createRoot } from "react-dom/client";
import TagManager from "react-gtm-module";

import { preloadIdb } from "./infra/sync";
import devLogger from "./utils/devLogger";

const gtmId = process.env.REACT_APP_GTM_ID; // e.g. GTM-XXXXXXX

// Optional dev-only: forward console + runtime errors to backend for live debugging.
// initFrontendLogForwarder(); // Disabled for production

// Only run in browser environments
if (typeof window !== "undefined") {
  // Ensure dataLayer exists and push gtm.start
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
    app: "pioneer-landing",
    environment: process.env.NODE_ENV,
  });

  if (gtmId) {
  try {
      TagManager.initialize({
        gtmId,
        dataLayerName: "dataLayer", // default, optional
        dataLayer: {
          app: "pioneer-landing",
          environment: process.env.NODE_ENV,
        },
      });
  devLogger.log("✅ GTM initialized:", gtmId);
    } catch (err) {
  devLogger.error("❌ Failed to initialize GTM:", err);
    }

    // Insert noscript iframe fallback (GTM recommends a <noscript> iframe in <body>)
    // We inject it here because CRA's static index.html can't use runtime env variables.
    try {
      const noscript = document.createElement("noscript");
      noscript.innerHTML = [
        `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"`,
        'height="0" width="0" style="display:none;visibility:hidden">',
        "</iframe>",
      ].join(" ");
      // Insert as first child of body (same effect as GTM recommended placement)
      document.body.insertBefore(noscript, document.body.firstChild);
    } catch (err) {
      // non-fatal
      devLogger.warn("Could not inject GTM noscript iframe:", err);
    }
  } else {
    devLogger.warn("⚠️ REACT_APP_GTM_ID not set — GTM not initialized");
  }
}

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

// Preload IndexedDB cache into memory BEFORE any store module is evaluated.
//
// CRITICAL: We use dynamic import() for App so that the entire module tree
// (App → AppRoutes → UnifiedMainComponent → orgs.store → createStore()) is
// evaluated AFTER preloadIdb() has populated the in-memory mirror.
// With a static `import App from "./App"` the store modules run at bundle
// parse time — before preloadIdb() — so every store falls back to its
// initialValue and the user sees blank columns on refresh.
//
// preloadIdb() resolves in ~5-15ms (IndexedDB read of a handful of keys).
async function boot(): Promise<void> {
  try {
    await preloadIdb();
  } catch {
    // IndexedDB unavailable — stores fall back to initialValue, which is fine.
  }
  const { default: App } = await import("./App");
  root.render(
    <App />
  );
}

void boot();
